type DateInput = string | Date | number;

/**
 * Parse various date inputs into a Date object.
 * Handles date-only strings (YYYY-MM-DD) as local time to avoid timezone issues.
 * Also handles ISO date strings at midnight UTC by extracting the date part.
 */
export const parseDate = (input: DateInput): Date => {
  if (input instanceof Date) {
    return input;
  }

  if (typeof input === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [year, month, day] = input.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const isoMidnightMatch = input.match(
      /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/
    );
    if (isoMidnightMatch) {
      const [, year, month, day] = isoMidnightMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  return new Date(input);
};

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/**
 * `toLocaleDateString` builds a native formatter on every call (~1 ms on a
 * phone), and every list row formats a date on mount. Formatters are
 * immutable, so one per (locale, options) is reused for the process.
 */
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatters.set(key, formatter);
  }
  return formatter;
}

export const formatDate = (
  input: DateInput,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = parseDate(input);

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  try {
    return dateFormatter(
      locale || 'en-US',
      options || DEFAULT_DATE_OPTIONS
    ).format(date);
  } catch (_error) {
    return 'Invalid Date';
  }
};

export const formatDateTime = (input: DateInput, use24Hour = false): string => {
  const date = parseDate(input);

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const dateStr = formatDate(date);
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: !use24Hour,
  };

  const timeStr = date.toLocaleTimeString('en-US', timeOptions);
  return `${dateStr} at ${timeStr}`;
};

export const formatRelativeTime = (input: DateInput): string => {
  const date = parseDate(input);
  const now = new Date();

  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMs < 0) {
    const absDiffInDays = Math.abs(diffInDays);
    if (absDiffInDays === 1) {
      return 'in 1 day';
    } else if (absDiffInDays < 7) {
      return `in ${absDiffInDays} days`;
    } else if (absDiffInDays < 30) {
      const weeks = Math.floor(absDiffInDays / 7);
      return `in ${weeks} week${weeks === 1 ? '' : 's'}`;
    }
    return 'in the future';
  }

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  } else {
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
  }
};

export const isToday = (input: DateInput): boolean => {
  const date = parseDate(input);
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const isYesterday = (input: DateInput): boolean => {
  const date = parseDate(input);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

export const isThisWeek = (input: DateInput): boolean => {
  const date = parseDate(input);
  const today = new Date();

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return date >= startOfWeek && date <= endOfWeek;
};

export const isThisMonth = (input: DateInput): boolean => {
  const date = parseDate(input);
  const today = new Date();

  return (
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const isThisYear = (input: DateInput): boolean => {
  const date = parseDate(input);
  const today = new Date();

  return date.getFullYear() === today.getFullYear();
};

export const getDaysBetween = (date1: DateInput, date2: DateInput): number => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  const diffInMs = d2.getTime() - d1.getTime();
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
};

export const addDays = (input: DateInput, days: number): Date => {
  const date = new Date(parseDate(input));
  date.setDate(date.getDate() + days);
  return date;
};

export const subtractDays = (input: DateInput, days: number): Date => {
  return addDays(input, -days);
};

export const getStartOfDay = (input: DateInput): Date => {
  const date = new Date(parseDate(input));
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getEndOfDay = (input: DateInput): Date => {
  const date = new Date(parseDate(input));
  date.setHours(23, 59, 59, 999);
  return date;
};

export const formatDuration = (seconds: number): string => {
  if (seconds === 0) {
    return '0 seconds';
  }

  const isNegative = seconds < 0;
  const absSeconds = Math.abs(seconds);

  const days = Math.floor(absSeconds / 86400);
  const hours = Math.floor((absSeconds % 86400) / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const remainingSeconds = absSeconds % 60;

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (minutes > 0 && days === 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  if (remainingSeconds > 0 && days === 0 && hours === 0) {
    parts.push(
      `${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`
    );
  }

  const result = parts.slice(0, 2).join(' ');
  return isNegative ? `-${result}` : result;
};

export const formatShortDate = (
  dateString: string,
  locale?: string
): string => {
  return formatDate(dateString, locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export type DurationUnit = 'minutes' | 'hours' | 'days';

/**
 * A span as one whole unit for a translated "{{count}} minutes" style
 * string (Hermes ships no Intl.RelativeTimeFormat): minutes under an hour,
 * at least 1; hours under a day; days after that. Rounded down.
 */
export function durationUnit(ms: number): {
  unit: DurationUnit;
  count: number;
} {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 60) return { unit: 'minutes', count: Math.max(1, minutes) };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.floor(hours / 24) };
}
